import { useMutation, useQuery } from '@tanstack/react-query'
import axios from 'axios'
import useAuth from '../../../hooks/useAuth'
import { InputOTP, InputOTPGroup, InputOTPSlot } from '../../ui/input-otp'
import { REGEXP_ONLY_DIGITS_AND_CHARS } from 'input-otp'
import { Button } from '../../ui/button'
import { CircleCheckIcon, CopyIcon, Loader2, XCircleIcon } from 'lucide-react'
import { Input } from '../../ui/input'
import { Link } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { setTfaVerified } from '../../../redux/actions'
import toast, { Toaster } from 'react-hot-toast'
import { useEffect, useState } from 'react'
import { deleteCookie, setCookie } from '../../../lib/cookies'
import { useTranslation } from 'react-i18next'

const TwoFactorAuth = () => {
    const { t } = useTranslation()
    const { auth } = useAuth()

    const { data:has_code, isLoading:isLoadingHasCode, isError:isErrorHasCode } = useQuery({
        queryKey: [`has-code`],
        queryFn: async () => {
            const { data:hasCode } = await axios.get(`${process.env.REACT_APP_TFA_URL}has-2fa-code`, {
                params: {
                    user: auth.data?.username,
                    api: process.env.REACT_APP_TFA_API
                }
            })

            if(typeof hasCode === 'boolean') {
                return hasCode;
            }
            if(typeof hasCode === 'string' && hasCode === 'true') {
                return true;
            }

            return false;
        },
        refetchOnWindowFocus: false
    })

    let hasCode = has_code;

  return (
    <>
    <Toaster />
    <div className='flex items-center justify-center h-[100dvh] bg-primary/5'>
        <div className='max-w-xl overflow-hidden bg-white border shadow-lg rounded-xl'>
            {(typeof hasCode !== 'boolean') 
            ?
            <div className='flex flex-col items-center justify-center p-4 space-y-2'>
                {(isLoadingHasCode && hasCode === undefined) && <p>{t('mfa.loading')}</p>}
                {(isErrorHasCode) && <p>{t('mfa.error')}</p>}
                <Link to="/login">
                    <Button>{t('mfa.back_to_login')}</Button>
                </Link>
            </div>
            :
            <>
            {hasCode 
                ? <VerifyCode />
                : <CreateCode />
            }
            </>
            }
        </div>
    </div>
    </>
  )
}

const VerifyCode = () => {
    const { t } = useTranslation()
    const { auth } = useAuth()
    const dispatch = useDispatch()
    
    const [input_code, setinput_code] = useState<string>('');
    
    const { mutate:verifyCode, isLoading:isVerifyingCode, isError:isErrorVerifyingCode, reset:resetVerify } = useMutation({
        mutationFn: async (code:string) => {
            const { data:verified } = await axios.post(`${process.env.REACT_APP_TFA_URL}verify-2fa-code`, {
                    code: code,
                    user: auth.data?.username,
                    api: process.env.REACT_APP_TFA_API
                }
            )
            
            if(verified.succes) {
                dispatch(setTfaVerified(true))
                setCookie('tfa', 'true', 1)
            } else {
                dispatch(setTfaVerified(false))
                deleteCookie('tfa')
                throw new Error('Invalid code')
            }
            
            return verified
        },
        onError: (error) => {
            if(error instanceof Error) {
                console.log(error.name, error.message)
                toast.error(error.message, { id:'tfa-error' })
            }

            setTimeout(()=>{resetVerify()}, 2000)
        }
    })

    useEffect(()=>{
        if(input_code.length === 6) {
            verifyCode(input_code)
        }
    }, [input_code])
    
    return (
        <>
        <div className='flex flex-col items-center justify-center gap-8 p-8'>
            <div className='space-y-2'>
                {isErrorVerifyingCode 
                ? <XCircleIcon className='w-5 h-5 mx-auto text-red-600'/>
                :   (isVerifyingCode 
                    ? <Loader2 className='w-5 h-5 mx-auto text-neutral-400 animate-spin'/>
                    : <CircleCheckIcon className='w-5 h-5 mx-auto text-neutral-400'/>
                    )
                }
                
                <p className='text-xl font-bold text-center'>{t('mfa.authentication')}</p>
                <p className='text-sm font-medium text-center text-slate-600'>{t('mfa.enter_code')}</p>
            </div>

            <InputOTP 
                onChange={(code)=>setinput_code(code)}
                value={input_code}
                maxLength={6}
                pattern={REGEXP_ONLY_DIGITS_AND_CHARS}
            >
                <InputOTPGroup>
                    <InputOTPSlot index={0} className='text-xl'/>
                    <InputOTPSlot index={1} className='text-xl'/>
                    <InputOTPSlot index={2} className='text-xl'/>
                </InputOTPGroup>
                
                <InputOTPGroup>
                    <InputOTPSlot index={3} className='text-xl'/>
                    <InputOTPSlot index={4} className='text-xl'/>
                    <InputOTPSlot index={5} className='text-xl'/>
                </InputOTPGroup>
            </InputOTP>
        </div>
        <div className='flex items-center justify-end gap-1 px-8 py-2 text-xs'>
            <p>{t('mfa.cant_access')}</p>
            <Link to="mailto:be.helpdesk@loomis.com" className='font-medium text-right text-primary hover:underline whitespace-nowrap'>{t('mfa.contact_loomis')}</Link>
        </div>
        <div className='flex items-center justify-end gap-2 px-8 py-4 bg-neutral-100'>
            <Link to="/login">
                <Button variant='outline' className='bg-white'>{t('mfa.back_to_login')}</Button>
            </Link>
            
            <Button onClick={()=>{verifyCode(input_code)}} disabled={isVerifyingCode || input_code.length !== 6}>
                {isVerifyingCode && <Loader2 className='w-4 h-4 animate-spin' />}
                {!isVerifyingCode ? t('mfa.confirm') : t('mfa.verifying')}
            </Button>
        </div>
        </>
    )
}

const CreateCode = () => {
    const { t } = useTranslation()
    const { auth } = useAuth()
    const dispatch = useDispatch()

    const [input_code, setinput_code] = useState<string>('');

    const { data:code } = useQuery({
        queryKey: [`code_${auth.data?.username}`],
        queryFn: async () => {
            const { data:code } = await axios.post(`${process.env.REACT_APP_TFA_URL}generate-2fa-code`, {
                    user: auth.data?.username,
                    api: process.env.REACT_APP_TFA_API
                }
            )
            return code
        },
        refetchOnWindowFocus: false
    })
    
    const { mutate:verifyCode, isLoading:isVerifyingCode, isError:isErrorVerifyingCode, reset:resetVerify } = useMutation({
        mutationFn: async (code:string) => {
            const { data:verified } = await axios.post(`${process.env.REACT_APP_TFA_URL}verify-2fa-code`, {
                    code: code,
                    user: auth.data?.username,
                    api: process.env.REACT_APP_TFA_API
                }
            )

            if(verified.succes) {
                dispatch(setTfaVerified(true))
                setCookie('tfa', 'true', 1)
            } else {
                dispatch(setTfaVerified(false))
                deleteCookie('tfa')
                throw new Error('Invalid code')
            }

            return verified
        },
        onError: (error) => {
            if(error instanceof Error) {
                toast.error(error.message, { id:'tfa-error' })
            }

            setTimeout(()=>{resetVerify()}, 2000)
        }
    })

    useEffect(()=>{
        if(input_code.length === 6) {
            verifyCode(input_code)
        }
    }, [input_code])
    
    return (
        <>
        <div className='flex flex-col justify-center gap-8 p-8'>
            <div>
                <p className='text-xl font-bold'>{t('mfa.setup_authenticator_app')}</p>
                <p className='text-sm font-medium text-slate-600'>{t('mfa.setup_authenticator_app_text')}</p>
            </div>

            <div>
                <div className='flex items-center gap-2'>
                    <div className='px-2 text-sm font-medium border rounded-full bg-slate-100 text-slate-600'>{t('mfa.step')} 1</div>
                    <p className='font-bold'>{t('mfa.scan_qr_code')}</p>
                </div>
                <p className='text-sm font-medium text-slate-600'>{t('mfa.scan_qr_code_text')}</p>
                <div className='flex items-center gap-4 p-2 mt-4 border bg-neutral-100 rounded-xl'>
                    <div className='p-2 bg-white border rounded-lg'>
                        <img src={`data:image/png;base64,${code?.image}`} className='w-32 h-32' />
                    </div>
                    <div className='space-y-2'>
                        <div>
                            <p className='font-bold'>{t('mfa.cant_scan')}</p>
                            <p className='text-sm font-medium text-neutral-500'>{t('mfa.cant_scan_text')}</p>
                        </div>
                        <Input className='bg-white rounded-md' value={code?.secret}/>
                        <Button 
                            onClick={()=>{
                                navigator.clipboard.writeText(code?.secret);
                                toast.success(t('mfa.copied_clipboard'))
                            }}
                            className='h-8 px-2'
                        >
                            <CopyIcon className='w-4 h-4'/>{t('mfa.copy_code')}
                        </Button>
                    </div>
                </div>            
            </div>

            <div className='space-y-4'>
                <div>
                    <div className='flex items-center gap-2'>
                        <div className='px-2 text-sm font-medium border rounded-full bg-slate-100 text-slate-600'>{t('mfa.step')} 2</div>
                        <p className='font-bold'>{t('mfa.get_verification_code')}</p>
                    </div>
                    <p className='text-sm font-medium text-slate-600'>{t('mfa.get_verification_code_text')}</p>
                </div>
                
                <div>
                    <p className='text-sm font-bold'>{t('mfa.enter_verification_code')}</p>
                    <InputOTP 
                        onChange={(code)=>setinput_code(code)}
                        value={input_code}
                        maxLength={6} 
                        pattern={REGEXP_ONLY_DIGITS_AND_CHARS}
                    >
                        <InputOTPGroup>
                            <InputOTPSlot index={0}/>
                            <InputOTPSlot index={1}/>
                            <InputOTPSlot index={2}/>
                        </InputOTPGroup>
                        
                        <InputOTPGroup>
                            <InputOTPSlot index={3}/>
                            <InputOTPSlot index={4}/>
                            <InputOTPSlot index={5}/>
                        </InputOTPGroup>
                    </InputOTP>
                </div>
            </div>
        </div>
        
        <div className='flex items-center justify-end gap-2 px-8 py-4 bg-neutral-100'>
            <Link to="/login">
                <Button variant='outline' className='bg-white'>{t('mfa.back_to_login')}</Button>
            </Link>
            
            <Button onClick={()=>{verifyCode(input_code)}} disabled={isErrorVerifyingCode || isVerifyingCode || input_code.length !== 6}>
                {isVerifyingCode && <Loader2 className='w-4 h-4 animate-spin' />}
                {!isVerifyingCode ? t('mfa.confirm') : t('mfa.verifying')}
            </Button>
        </div>
        </>
    )
}

export default TwoFactorAuth